import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AppContextType } from "@/context/app";
import { ProjectContext, ProjectsMap } from "@/context/project";
import { UserContext } from "@/context/user";
import { templates, appConfigs } from "@/config/nodes";
import { arrayToObject } from "@/lib/utils";
import { Github } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function AppDetails() {
  const { user } = React.useContext(UserContext) as {
    user: { data: { login: string } };
  };
  const { projectsMap } = React.useContext(ProjectContext) as {
    projectsMap: ProjectsMap;
  };
  const params = useParams();
  const [projectId, setProjectId] = useState<string | "">("");
  const [appName, setAppName] = useState<string | "">("");

  const appsMap = arrayToObject(
    projectsMap?.[projectId]?.apps?.map((app) => ({ ...app, id: app.name })) ||
      [],
  ) as Record<string, AppContextType>;
  const app = appsMap[appName];
  const template = app && templates[app.template as keyof typeof templates];

  useEffect(() => {
    setProjectId(params?.projectId ?? "");
    setAppName(params?.appName ?? "");
  }, [params]);

  if (!app) {
    return (
      <main className="p-4">
        <p>Application not found.</p>
      </main>
    );
  }

  const isProjectGenerated =
    projectsMap[projectId] &&
    [
      "generated",
      "configured",
      "provisioned",
      "deployed",
      "redeployed",
    ].includes(projectsMap[projectId]?.status);

  return (
    <main className="p-4">
      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="mb-4 grid w-full grid-cols-2">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="deployment">Deployment</TabsTrigger>
        </TabsList>
        <TabsContent key="overview" value="overview">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-2xl font-bold">{app.name}</CardTitle>
                <Badge variant="outline">{app.type}</Badge>
              </div>
              <CardDescription>
                Overview of the application configuration and repository.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-1">
                  <p className="mb-2 text-sm font-medium text-muted-foreground">
                    Template
                  </p>
                  {template ? (
                    <div className="flex items-center gap-2">
                      {template.icon}
                      <span className="font-mono text-sm">
                        {template.label}
                      </span>
                    </div>
                  ) : (
                    <p className="font-mono text-sm">{app.template}</p>
                  )}
                </div>
                {isProjectGenerated ? (
                  <div className="space-y-1">
                    <p className="mb-2 text-sm font-medium text-muted-foreground">
                      GitHub Repository
                    </p>
                    <a
                      href={`https://github.com/${user?.data?.login}/${appName}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-sm text-blue-500 hover:underline"
                    >
                      <Github size={16} />
                      <span>{`https://github.com/${user?.data?.login}/${appName}`}</span>
                    </a>
                  </div>
                ) : (
                  <div className="space-y-1">
                    <p className="mb-2 text-sm font-medium text-muted-foreground">
                      Project Status
                    </p>
                    <Link to={`/projects/${projectId}`}>
                      <Button variant={"outline"}>Generate Repositories</Button>
                    </Link>
                  </div>
                )}
              </div>
              {app.config &&
              (app.config.auth ||
                app.config.db ||
                (app.config.providers && app.config.providers.length > 0)) ? (
                <div className="space-y-3">
                  <h3 className="text-lg font-semibold">Configuration</h3>
                  <div className="space-y-2">
                    <ul className="space-y-3">
                      {Object.entries(app.config).map(([key, value]) => {
                        if (
                          (key === "auth" || key === "db") &&
                          typeof value === "boolean" &&
                          value
                        ) {
                          const config =
                            appConfigs[key as keyof typeof appConfigs];
                          if (!config) return null;
                          return (
                            <li key={key} className="flex items-center gap-2">
                              {config.icon}
                              <span>{config.iconLabel}</span>
                            </li>
                          );
                        }
                        if (
                          key === "providers" &&
                          Array.isArray(value) &&
                          value.length > 0
                        ) {
                          const providersConfig = appConfigs.providers;
                          return (
                            <li key={key}>
                              <div className="flex items-center gap-2">
                                {providersConfig.icon}
                                <span className="font-medium">
                                  {providersConfig.label}
                                </span>
                              </div>
                              <div className="mt-2 flex flex-wrap gap-2 pl-6">
                                {value.map((providerValue) => {
                                  const provider = providersConfig.options.find(
                                    (p) => p.value === providerValue,
                                  );
                                  if (!provider) return null;
                                  return (
                                    <Badge
                                      key={providerValue}
                                      variant="secondary"
                                      className="flex items-center gap-1.5"
                                    >
                                      {provider.icon}
                                      <span>{provider.label}</span>
                                    </Badge>
                                  );
                                })}
                              </div>
                            </li>
                          );
                        }
                        return null;
                      })}
                    </ul>
                  </div>
                </div>
              ) : null}
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent key="deployment" value="deployment">
          <Card>
            <CardHeader>
              <CardTitle>Deployment</CardTitle>
              <CardDescription>
                Details about the application deployment status and history.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {projectsMap[projectId]?.status === "deployed" ||
              projectsMap[projectId]?.status === "redeployed" ? (
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold">Application URL</h3>
                    <a
                      href={`https://${app.name}.${projectsMap[projectId].domainName}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-500 hover:underline"
                    >
                      {`https://${app.name}.${projectsMap[projectId].domainName}`}
                    </a>
                  </div>
                  <div>
                    <h3 className="font-semibold">IP Address</h3>
                    <p>{projectsMap[projectId].ipAddress}</p>
                  </div>
                </div>
              ) : (
                <p className="italic">Deployment pending.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </main>
  );
}
